import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { useCompany } from "../../../contexts/CompanyContext";

interface TeamGoal {
  id: string;
  title: string;
  description: string | null;
  target_value: number;
  unit: string;
  category: string;
  deadline: string | null;
}

const CAT_CFG: Record<string, { color: string; icon: string }> = {
  capital:    { color: "#00C896", icon: "ri-money-dollar-circle-line" },
  reputation: { color: "#FF9F43", icon: "ri-award-line" },
  tasks:      { color: "#7A9BFF", icon: "ri-checkbox-line" },
  checkins:   { color: "#E8375F", icon: "ri-time-line" },
  custom:     { color: "#A78BFA", icon: "ri-target-line" },
};

export default function TeamKPIPanel() {
  const { workspaceId, companyState } = useCompany();

  const [goals, setGoals] = useState<TeamGoal[]>([]);
  const [aggStats, setAggStats] = useState({ totalCapital: 0, avgReputation: 0, totalTasks: 0, totalCheckins: 0, memberCount: 0 });
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const fetchData = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    const [{ data: goalsData }, { data: states }, { data: tasks }, { data: checkins }, { data: members }] = await Promise.all([
      supabase.from("team_kpi_goals").select("*").eq("workspace_id", workspaceId).order("created_at"),
      supabase.from("company_states").select("capital,reputation").eq("workspace_id", workspaceId),
      supabase.from("task_completions").select("id", { count: "exact" }).eq("workspace_id", workspaceId),
      supabase.from("daily_checkins").select("id", { count: "exact" }).eq("workspace_id", workspaceId),
      supabase.from("workspace_members").select("user_id").eq("workspace_id", workspaceId),
    ]);
    setGoals((goalsData ?? []) as TeamGoal[]);
    const sArr = (states ?? []) as { capital: number; reputation: number }[];
    setAggStats({
      totalCapital: sArr.reduce((s, r) => s + (r.capital ?? 0), 0),
      avgReputation: sArr.length ? Math.round(sArr.reduce((s, r) => s + (r.reputation ?? 0), 0) / sArr.length) : 0,
      totalTasks: tasks?.length ?? 0,
      totalCheckins: checkins?.length ?? 0,
      memberCount: members?.length ?? 0,
    });
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getProgress = (goal: TeamGoal): number => {
    let current = 0;
    if (goal.category === "capital") current = aggStats.totalCapital;
    else if (goal.category === "reputation") current = aggStats.avgReputation;
    else if (goal.category === "tasks") current = aggStats.totalTasks;
    else if (goal.category === "checkins") current = aggStats.totalCheckins;
    return Math.min(100, goal.target_value > 0 ? (current / goal.target_value) * 100 : 0);
  };

  const getCurrentValue = (goal: TeamGoal): number => {
    if (goal.category === "capital") return aggStats.totalCapital;
    if (goal.category === "reputation") return aggStats.avgReputation;
    if (goal.category === "tasks") return aggStats.totalTasks;
    if (goal.category === "checkins") return aggStats.totalCheckins;
    return 0;
  };

  if (loading) return <div className="bg-[#111112] rounded-xl border border-white/[0.05] h-20 animate-pulse" />;
  if (!goals.length) return null;

  return (
    <div className="bg-[#111112] rounded-xl border border-white/[0.05] overflow-hidden">
      <button type="button" onClick={() => setCollapsed(v => !v)}
        className="w-full px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-white/[0.02] transition-colors border-b border-white/[0.05]">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#A78BFA]/15">
            <i className="ri-team-line text-[#A78BFA] text-base"></i>
          </div>
          <div className="text-left">
            <div className="text-[#F2F2F2] text-sm font-semibold">팀 공동 KPI 목표</div>
            <div className="text-[#444444] text-[10px]">강사 설정 · {goals.length}개 목표 · {aggStats.memberCount}명 참여</div>
          </div>
        </div>
        <i className={`ri-arrow-${collapsed ? "down" : "up"}-s-line text-[#555555] text-sm`}></i>
      </button>

      {!collapsed && (
        <div className="px-4 py-4 space-y-3">
          {goals.map(goal => {
            const pct = getProgress(goal);
            const current = getCurrentValue(goal);
            const cfg = CAT_CFG[goal.category] ?? CAT_CFG.custom;
            const done = pct >= 100;
            return (
              <div key={goal.id} className="bg-white/[0.02] rounded-xl border border-white/[0.04] px-4 py-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <i className={`${cfg.icon} text-sm shrink-0`} style={{ color: cfg.color }}></i>
                    <div className="min-w-0">
                      <p className="text-[#CCCCCC] text-xs font-medium truncate">{goal.title}</p>
                      {goal.description && <p className="text-[#3D3D3D] text-[9px] truncate mt-0.5">{goal.description}</p>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {done ? (
                      <span className="text-[10px] font-bold text-[#00C896] flex items-center gap-1"><i className="ri-checkbox-circle-fill text-sm"></i>달성!</span>
                    ) : (
                      <span className="text-[10px] font-bold tabular-nums" style={{ color: cfg.color }}>{Math.round(pct)}%</span>
                    )}
                  </div>
                </div>
                <div className="w-full h-2 bg-white/[0.05] rounded-full overflow-hidden mb-2">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: done ? "#00C896" : cfg.color }} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#444444] text-[9px] tabular-nums">
                    현재: <span style={{ color: cfg.color }}>{current.toLocaleString()}{goal.unit}</span>
                    <span className="text-[#2A2A2A]"> / 목표: {goal.target_value.toLocaleString()}{goal.unit}</span>
                  </span>
                  {goal.deadline && (
                    <span className="text-[#333333] text-[9px]">~{goal.deadline}</span>
                  )}
                </div>
              </div>
            );
          })}

          {/* 팀 요약 통계 */}
          <div className="grid grid-cols-4 gap-2 pt-1">
            {[
              { label: "팀 자본금 합계", value: `${aggStats.totalCapital.toLocaleString()}만`, color: "#00C896" },
              { label: "평균 직급점수", value: `${aggStats.avgReputation}pt`, color: "#FF9F43" },
              { label: "총 태스크 완료", value: `${aggStats.totalTasks}건`, color: "#7A9BFF" },
              { label: "총 출근 횟수", value: `${aggStats.totalCheckins}일`, color: "#E8375F" },
            ].map(s => (
              <div key={s.label} className="bg-white/[0.02] rounded-lg border border-white/[0.04] p-2.5 text-center">
                <div className="text-sm font-bold tabular-nums" style={{ color: s.color }}>{s.value}</div>
                <div className="text-[#333333] text-[8px] mt-0.5 leading-tight">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
